/**
 * Production adapter for `show_in_viewer`: finds controls in the page by
 * their existing `data-testid` and outlines them with the agent's own
 * highlighter in the app theme's primary colour. Reads the DOM only — no
 * viewer state, no session. Browser glue covered by the manual demo; the
 * logic is unit-tested through fakes and jsdom.
 */
import { useTheme } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import {
    createHighlighter,
    probeSelector,
    type Highlighter,
} from './highlighter';
import type { ShowInViewerDeps } from './showInViewer';

export function useShowInViewerDeps(): () => ShowInViewerDeps {
    const color = useTheme().palette.primary.main;
    const highlighter = useRef<Highlighter>();

    // Remove any outline when the chat drawer unmounts.
    useEffect(() => () => highlighter.current?.clear(), []);

    return useCallback((): ShowInViewerDeps => {
        if (!highlighter.current) {
            highlighter.current = createHighlighter(document, color);
        }
        const h = highlighter.current;
        return {
            probe: (selector) => probeSelector(selector, document),
            highlight: (chain) => h.show(chain),
        };
    }, [color]);
}
